import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from "@angular/core";
import {Aangifte, BokkzIbDataService, IncomeTaxEntity, ReisAftrekOV} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormControl, FormGroup} from "@angular/forms";
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {CommonModule, DatePipe} from '@angular/common';
import {debounceTime, filter, map, Observable, pairwise, shareReplay, startWith, Subscription, take} from "rxjs";
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

type ReisaftrekFormGroup = FormGroup<{
  periodeVanReizen: PeriodeFormGroup;
  omschrijving: FormControl<string | null>;
  aantalDagenPerWeek: FormControl<number | null>;
  afstandEnkeleReis: FormControl<number | null>;
  vergoedingWerkgever: FormControl<number | null>;
}>;

type PeriodeFormGroup = FormGroup<{
  begindatum: FormControl<Date | null>;
  einddatum: FormControl<Date | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-reisaftrek-ov',
  templateUrl: './reisaftrek-ov.component.html',
  styleUrls: ['./reisaftrek-ov.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SharedUiDetailsModule,
    CommonModule,
    SharedUiAlertModule
  ],
  providers: [DatePipe]
})
export class ReisaftrekOvComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();
  public index = 0;
  public isPartner = false;

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public optionsAantalDagen: SelectFormControlItem<number>[] = [
    {value: 1, label: "1 dag"},
    {value: 2, label: "2 dagen"},
    {value: 3, label: "3 dagen"},
    {value: 4, label: "4 dagen"},
    {value: 5, label: "5 dagen"}
  ];

  public formGroup = new FormGroup({
    reisaftrekOpenbaarVervoer: new FormArray<ReisaftrekFormGroup>([]),
  });

  ngOnInit(): void {
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(partner => {
      this.isPartner = partner;
      this.incomeTaxEntity$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(entity => {
        if (partner) {
          this.resetAndPatchForms(entity.aangifte?.partnerAangifte?.box1?.belastbaarLoon?.reisAftrekOV || []);
        } else {
          this.resetAndPatchForms(entity.aangifte?.box1?.belastbaarLoon?.reisAftrekOV || []);
        }
      });
    });
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.changeSubscriptions.unsubscribe();
  }

  private resetAndPatchForms(details : ReisAftrekOV[]): void {
    this.formGroup.reset({}, { emitEvent: false });
    this.updateForm(details || []);
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    const extractInputForCalculation = () => this.reisaftrekOpenbaarVervoer.getRawValue().map(item => ({
      begindatum: item.periodeVanReizen.begindatum,
      einddatum: item.periodeVanReizen.einddatum,
      aantalDagenPerWeek: item.aantalDagenPerWeek,
      afstandEnkeleReis: item.afstandEnkeleReis,
      vergoedingWerkgever: item.vergoedingWerkgever,
    }));

    this.changeSubscriptions.add(
      this.reisaftrekOpenbaarVervoer.valueChanges.pipe(
        debounceTime(500),
        map(() => this.saveForm()),
        map(() => extractInputForCalculation()),
        startWith(extractInputForCalculation()),
        pairwise(),
        filter(([prev, curr]) => JSON.stringify(prev) !== JSON.stringify(curr)),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        this.dataService.calculateLatestIncomeTaxReturn().subscribe();
      })
    );
  }

  get reisaftrekOpenbaarVervoer(): FormArray<ReisaftrekFormGroup> {
    return this.formGroup.controls.reisaftrekOpenbaarVervoer;
  }

  addRij(): void {
    this.reisaftrekOpenbaarVervoer.push(this.createFormGroup(), { emitEvent: false })
    this.saveForm();
  }

  verwijderRij(index: number): void {
    this.reisaftrekOpenbaarVervoer.removeAt(index, { emitEvent: false });
    this.formGroup.updateValueAndValidity({emitEvent : false})
    this.formGroup.markAsDirty({emitEvent: false})
    this.saveForm();
  }

  createFormGroup(item?: ReisAftrekOV): FormGroup {
    return new FormGroup({
      omschrijving: new FormControl<string | null>(item?.omschrijving ?? null),
      periodeVanReizen: new FormGroup({
        begindatum: new FormControl<Date | null>(item?.periodeVanReizen?.begindatum ?? null),
        einddatum: new FormControl<Date | null>(item?.periodeVanReizen?.einddatum ?? null),
      }),
      aantalDagenPerWeek: new FormControl<number | null>(item?.aantalDagenPerWeek ?? null),
      afstandEnkeleReis: new FormControl<number | null>(item?.afstandEnkeleReis ?? null),
      vergoedingWerkgever: new FormControl<number | null>(item?.vergoedingWerkgever ?? null),
    });
  }

  private updateForm(input : ReisAftrekOV[]): void {
    const lijst = input || [];
    const formArray = this.reisaftrekOpenbaarVervoer;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormGroup(lijst[i]);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  saveForm(): void {
    this.formGroup.updateValueAndValidity({emitEvent: false});

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      const updateBelastbaarLoon = (aangifte: Aangifte) => ({
        ...aangifte,
        box1: {
          ...aangifte.box1,
          belastbaarLoon: {
            ...aangifte.box1?.belastbaarLoon,
            reisAftrekOV: this.formGroup.getRawValue().reisaftrekOpenbaarVervoer,
          },
        },
      });

      const updatedAangifte = this.isPartner ? {
        ...currentEntity.aangifte,
        partnerAangifte: updateBelastbaarLoon(currentEntity.aangifte?.partnerAangifte!),
      } : updateBelastbaarLoon(currentEntity.aangifte);

      return {
        ...currentEntity,
        aangifte: updatedAangifte,
      } as IncomeTaxEntity;
    });
  }
}
